import { type User } from '@kalos/kalos-rpc';
import {
  Badge,
  Button,
  Input,
  ScrollArea,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@kalos/ui';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import {
  AccessibilityIcon,
  ArchiveIcon,
  BackpackIcon,
  BarChartIcon,
  BookmarkIcon,
  CalendarIcon,
  CameraIcon,
  ClipboardIcon,
  ClockIcon,
  CounterClockwiseClockIcon,
  CubeIcon,
  EnvelopeClosedIcon,
  ExitIcon,
  FileIcon,
  HamburgerMenuIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  PersonIcon,
  ReaderIcon,
  ReloadIcon,
  StackIcon,
} from '@radix-ui/react-icons';
import { type ToOptions, useLocation } from '@tanstack/react-router';
import { Link, useRouter } from '@tanstack/react-router';
import {
  BadgeCheck,
  BadgeDollarSign,
  Building2,
  Calendar,
  Car,
  ChartColumn,
  CircleUser,
  FileCheck,
  FlagIcon,
  Hammer,
  HammerIcon,
  Headset,
  KeyRound,
  MessageCircleQuestion,
  ShieldIcon,
  TentIcon,
} from 'lucide-react';
import { type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '../../context/AuthContext';
import { useFeatureFlags } from '../../featureFlags/context';
import { useFeatureFlag } from '../../featureFlags/useFeatureFlag';
import { useGetInternalLinkQuery } from '../../hooks/react-query/useInternalLinkQuery';
import { useKalosLogoQuery } from '../../hooks/react-query/useKalosLogoQuery';
import { IS_DEVELOPMENT_ENVIRONMENT } from '../../tools/environment';
import {
  canViewAccountingReports,
  canViewInternalReports,
  canViewPayrollReports,
  hasPayrollPermission,
  isTransactionPreValidatePermissionCheck,
  kpiAccessCheck,
  managerCheck,
  newHireDashboardCheck,
  projectScheduleOpenCheck,
  reportsMenuAccessCheck,
  terminationDashboardCheck,
} from '../../tools/permissionChecks';
import { type LocalStorage } from '../../tools/storageManager';
import { COMMERCIAL_DEPARTMENTS } from '../ComponentsLibrary/CommercialServiceQueue/types';
import { Modal } from '../ComponentsLibrary/Modal';
import { UploadPhotoOCR } from '../ComponentsLibrary/UploadPhotoOCR';
import { canOpenFeatureFlags } from '../FeatureFlags/utils';
import customTheme from '../Theme/main';
import KalosMenuItem, { NavOptionComponent } from './components/KalosMenuItem';

export type SideMenuProps = {
  imgURL?: string;
};

export type MenuItem = NavOption | SubMenu;

export type SubMenu = {
  heading: React.ReactNode;
  localStorageKey: keyof LocalStorage['sideMenuDropdownState'];
  items: NavOption[];
};

export type NavOption = {
  title: string;
  icon?: JSX.Element;
} & (
  | {
      route: ToOptions;
      onClick?: () => void;
    }
  | {
      href: string;
      target: React.HTMLAttributeAnchorTarget;
      onClick?: () => void;
    }
  | {
      button: true;
      onClick: () => void;
    }
);

const SIDE_MENU_SECTION_LABELS: Record<keyof LocalStorage['sideMenuDropdownState'], string> = {
  home: 'Home',
  profile: 'Profile',
  customers: 'Customers',
  dispatch: 'Dispatch',
  reporting: 'Reporting',
  team: 'Team',
  finance: 'Finance',
  admin: 'Admin Resources',
  tools: 'Tools',
};

type SubModuleSearchEntry = {
  title: string;
  keywords?: string[];
  route: ToOptions;
  canAccess?: (user: User) => boolean;
};

const SUB_MODULE_SEARCH_INDEX: Record<string, SubModuleSearchEntry[]> = {
  '/projectResources': [
    {
      title: 'Lodging Request',
      keywords: ['lodging', 'hotel', 'room', 'stay', 'accommodation'],
      route: { to: '/projectResources', search: { projectResourceTab: 'lodging' } },
    },
    {
      title: 'Project Forms Dashboard',
      keywords: ['forms dashboard', 'form submissions', 'submissions'],
      route: { to: '/projectResources', search: { projectResourceTab: 'formsDashboard' } },
      canAccess: adminCheck,
    },
    {
      title: 'Permit Request',
      keywords: ['permit'],
      route: { to: '/projectResources', search: { projectResourceTab: 'permit' } },
    },
    {
      title: 'Inspection',
      keywords: ['inspection'],
      route: { to: '/projectResources', search: { projectResourceTab: 'inspection' } },
    },
    {
      title: 'Forms',
      keywords: ['subcontract', 'nto', 'project scope', 'form'],
      route: { to: '/projectResources', search: { projectResourceTab: 'forms' } },
    },
  ],
  '/vehicles': [
    {
      title: 'Vehicles',
      keywords: ['vehicle', 'car', 'truck'],
      route: { to: '/vehicles', search: { vehicleDashboardTab: 'vehicles' } },
    },
    {
      title: 'Trailers',
      keywords: ['trailer'],
      route: { to: '/vehicles', search: { vehicleDashboardTab: 'trailers' } },
    },
  ],
  '/notes': [
    {
      title: 'My Notes Reports',
      keywords: ['report', 'reports', 'notes report', 'summaries', 'ai summaries'],
      route: { to: '/notes' },
    },
  ],
  '/hrDashboard': [
    {
      title: 'Performance Reviews',
      keywords: ['performance', 'review'],
      route: { to: '/hrDashboard', search: { defaultTabId: '0' } },
    },
    {
      title: 'Employee Resources',
      keywords: ['resources', 'documents'],
      route: { to: '/hrDashboard', search: { defaultTabId: '1' } },
    },
    {
      title: 'Employee Directory',
      keywords: ['directory'],
      route: { to: '/hrDashboard', search: { defaultTabId: '2' } },
    },
    {
      title: 'Safety Resources',
      keywords: ['safety'],
      route: { to: '/hrDashboard', search: { defaultTabId: '3' } },
    },
    {
      title: 'Organization Chart',
      keywords: ['org chart', 'organization', 'hierarchy'],
      route: { to: '/hrDashboard', search: { defaultTabId: '4' } },
    },
    {
      title: 'New Hire',
      keywords: ['new hire', 'onboarding', 'hire'],
      route: { to: '/hrDashboard', search: { defaultTabId: '5' } },
      canAccess: newHireDashboardCheck,
    },
    {
      title: 'Termination',
      keywords: ['termination', 'terminate', 'fire', 'offboarding'],
      route: { to: '/hrDashboard', search: { defaultTabId: '6' } },
      canAccess: terminationDashboardCheck,
    },
    {
      title: 'KPI Report',
      keywords: ['kpi', 'metrics', 'report'],
      route: { to: '/hrDashboard', search: { defaultTabId: '7' } },
      canAccess: kpiAccessCheck,
    },
  ],
  '/payroll': [
    {
      title: 'Payroll Summary',
      keywords: ['summary'],
      route: { to: '/payroll' },
      canAccess: hasPayrollPermission,
    },
    {
      title: 'Pending Timesheets',
      keywords: ['pending', 'approve timesheet'],
      route: { to: '/payroll' },
      canAccess: hasPayrollPermission,
    },
    {
      title: 'Timeoff Approvals',
      keywords: ['timeoff', 'time off', 'approve'],
      route: { to: '/payroll' },
    },
    {
      title: 'Spiff/Bonus/Commission Approvals',
      keywords: ['spiff', 'bonus', 'commission'],
      route: { to: '/payroll' },
    },
    { title: 'Per Diem Approvals', keywords: ['per diem', 'perdiem'], route: { to: '/payroll' } },
    { title: 'Trip Approvals', keywords: ['trip', 'travel', 'mileage'], route: { to: '/payroll' } },
    {
      title: 'Reimbursement Approvals',
      keywords: ['reimbursement'],
      route: { to: '/payroll' },
    },
  ],
  '/analytics': [
    {
      title: 'Financial Performance',
      keywords: ['financial', 'revenue', 'finance'],
      route: { to: '/analytics' },
    },
    {
      title: 'Pricing & Job Profitability',
      keywords: ['pricing', 'profitability', 'margin'],
      route: { to: '/analytics' },
    },
    { title: 'Service Quality', keywords: ['service quality'], route: { to: '/analytics' } },
    {
      title: 'Field Worker Performance',
      keywords: ['field worker', 'technician performance'],
      route: { to: '/analytics' },
    },
  ],
  '/proposals': [
    {
      title: 'Queue',
      keywords: ['queue', 'needing quotes', 'jobs needing quotes'],
      route: { to: '/proposals' },
    },
  ],
  '/reports': [
    { title: 'Org Chart', keywords: ['org chart', 'organization'], route: { to: '/reports' } },
    {
      title: 'Time Off Report',
      keywords: ['time off', 'timeoff', 'pto', 'vacation'],
      route: { to: '/reports' },
    },
    {
      title: 'Timeoff Audit Report',
      keywords: ['timeoff audit', 'audit'],
      route: { to: '/reports/timeoff-audit' },
    },
    {
      title: 'Job Status Report',
      keywords: ['job status'],
      route: { to: '/reports' },
      canAccess: canViewInternalReports,
    },
    {
      title: 'Overbooked Report',
      keywords: ['overbooked'],
      route: { to: '/reports' },
      canAccess: canViewInternalReports,
    },
    {
      title: 'Billing Status Report',
      keywords: ['billing status'],
      route: { to: '/reports' },
      canAccess: canViewInternalReports,
    },
    {
      title: 'Notifications Report',
      keywords: ['notifications'],
      route: { to: '/reports' },
      canAccess: canViewInternalReports,
    },
    {
      title: 'Deleted Jobs',
      keywords: ['deleted jobs', 'deleted service calls'],
      route: { to: '/reports' },
      canAccess: canViewInternalReports,
    },
    {
      title: 'Callback Report',
      keywords: ['callback'],
      route: { to: '/reports' },
      canAccess: canViewInternalReports,
    },
    {
      title: 'Job Zip Code',
      keywords: ['zip', 'zip code'],
      route: { to: '/reports' },
      canAccess: canViewInternalReports,
    },
    {
      title: 'Residential Heat Map',
      keywords: ['heatmap', 'heat map', 'residential', 'map'],
      route: { to: '/reports' },
      canAccess: canViewInternalReports,
    },
    {
      title: 'Employee Weekly Report',
      keywords: ['employee weekly', 'weekly'],
      route: { to: '/reports' },
      canAccess: canViewInternalReports,
    },
    {
      title: 'Job Cost Report',
      keywords: ['job cost', 'cost report', 'job number', 'cost', 'daily job'],
      route: { to: '/reports' },
      canAccess: canViewInternalReports,
    },
    {
      title: 'System Install Anniversary Report',
      keywords: ['system install', 'anniversary', 'install anniversary', 'install date'],
      route: { to: '/reports' },
      canAccess: canViewInternalReports,
    },
    {
      title: 'Timesheet Validation Report',
      keywords: ['timesheet validation', 'approved timecards'],
      route: { to: '/reports' },
      canAccess: canViewPayrollReports,
    },
    {
      title: 'Payroll Report',
      keywords: ['payroll'],
      route: { to: '/reports' },
      canAccess: canViewPayrollReports,
    },
    {
      title: 'Processed Report',
      keywords: ['processed'],
      route: { to: '/reports' },
      canAccess: canViewPayrollReports,
    },
    {
      title: 'Commission Difference Report',
      keywords: ['commission', 'comm diff'],
      route: { to: '/reports/comm-diff' },
      canAccess: canViewPayrollReports,
    },
    {
      title: 'Paylocity Report',
      keywords: ['paylocity'],
      route: { to: '/reports' },
      canAccess: canViewPayrollReports,
    },
    {
      title: 'Processed Journal/Cost Item Report',
      keywords: ['processed journal', 'cost item', 'perdiem', 'spiff', 'trip'],
      route: { to: '/reports' },
      canAccess: canViewPayrollReports,
    },
    {
      title: 'Transaction Validation Report',
      keywords: ['transaction validation'],
      route: { to: '/reports' },
      canAccess: canViewAccountingReports,
    },
    {
      title: 'Job Hours Journal Report',
      keywords: ['job hours', 'hours journal'],
      route: { to: '/reports' },
      canAccess: canViewAccountingReports,
    },
    {
      title: 'Receipts Journal Report',
      keywords: ['receipts journal', 'receipt'],
      route: { to: '/reports' },
      canAccess: canViewAccountingReports,
    },
    {
      title: 'Sales Journal Report',
      keywords: ['sales journal'],
      route: { to: '/reports' },
      canAccess: canViewAccountingReports,
    },
    {
      title: 'Invoice Journal Report',
      keywords: ['invoice journal'],
      route: { to: '/reports' },
      canAccess: canViewAccountingReports,
    },
    {
      title: 'Billing Audit',
      keywords: ['billing audit'],
      route: { to: '/reports' },
      canAccess: canViewAccountingReports,
    },
    {
      title: 'Prompt Payment Report',
      keywords: ['prompt payment'],
      route: { to: '/reports' },
      canAccess: canViewAccountingReports,
    },
    {
      title: 'Credit Card Statement',
      keywords: ['credit card', 'statement'],
      route: { to: '/reports' },
      canAccess: canViewAccountingReports,
    },
    {
      title: 'Lauren Daily Report',
      keywords: ['lauren daily', 'daily'],
      route: { to: '/reports' },
      canAccess: canViewAccountingReports,
    },
    {
      title: 'Warranty Report',
      keywords: ['warranty'],
      route: { to: '/reports' },
      canAccess: canViewAccountingReports,
    },
    {
      title: 'Timeoff Summary Report',
      keywords: ['timeoff summary'],
      route: { to: '/reports' },
      canAccess: canViewAccountingReports,
    },
  ],
};

type FlattenedMenuNavOption = {
  nav: NavOption;
  sectionLabel: string;
  keywords?: string[];
  isSubModule?: boolean;
};

function flattenVisibleMenuNavOptions(menuItems: MenuItem[], user: User): FlattenedMenuNavOption[] {
  const out: FlattenedMenuNavOption[] = [];
  const pushNav = (nav: NavOption, sectionLabel: string) => {
    out.push({ nav, sectionLabel });
    if ('route' in nav && nav.route.to && SUB_MODULE_SEARCH_INDEX[nav.route.to]) {
      for (const sub of SUB_MODULE_SEARCH_INDEX[nav.route.to]) {
        if (sub.canAccess && !sub.canAccess(user)) {
          continue;
        }
        out.push({
          nav: { title: sub.title, icon: nav.icon, route: sub.route },
          sectionLabel: nav.title,
          keywords: sub.keywords,
          isSubModule: true,
        });
      }
    }
  };
  for (const item of menuItems) {
    if ('items' in item) {
      const sectionLabel = SIDE_MENU_SECTION_LABELS[item.localStorageKey];
      for (const nav of item.items) {
        pushNav(nav, sectionLabel);
      }
    } else if (item.title !== 'Logout') {
      pushNav(item, '');
    }
  }
  return out;
}

function menuSearchMatches(
  query: string,
  title: string,
  sectionLabel: string,
  keywords?: string[],
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) {
    return true;
  }
  return (
    title.toLowerCase().includes(q) ||
    (sectionLabel.length > 0 && sectionLabel.toLowerCase().includes(q)) ||
    (keywords?.some((keyword) => keyword.toLowerCase().includes(q)) ?? false)
  );
}

function adminCheck(user: User) {
  return (
    user.permissionGroups.map((p) => p.name).includes('Manager') ||
    user.permissionGroups.map((p) => p.name).includes('Payroll') ||
    user.isSU === 1 ||
    user.isAdmin == 1
  );
}

function memoCheck(user: User) {
  return (
    user.permissionGroups.map((p) => p.name).includes('MemoAdmin') ||
    user.permissionGroups.map((p) => p.name).includes('CommunicationTeam')
  );
}

function fleetAdminCheck(user: User) {
  return user.permissionGroups.map((p) => p.name).includes('FleetAdmin');
}

const SideMenu = ({ imgURL }: SideMenuProps) => {
  const kalosLogo = useKalosLogoQuery('onDark');
  const logoSrc = imgURL ?? kalosLogo.data;
  const { user } = useAuth();
  const auth = useAuth();
  const router = useRouter();
  const { pathname } = useLocation();

  const isLoggedIn = user.id !== 0;

  const isManager = useMemo(() => {
    return user.id ? adminCheck(user) : false;
  }, [user]);
  const isMemo = useMemo(() => {
    return user.id ? memoCheck(user) : false;
  }, [user]);

  const isFleetAdmin = useMemo(() => {
    return user.id ? fleetAdminCheck(user) : false;
  }, [user]);

  const isTransactionValidator = useMemo(() => {
    return isTransactionPreValidatePermissionCheck(user);
  }, [user]);

  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [openUploadReceiptOCR, setOpenUploadReceiptOCR] = useState<boolean>(false);
  const [menuSearchQuery, setMenuSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setIsOpened(false);
  }, [pathname]);

  useEffect(() => {
    if (!isOpened) {
      setMenuSearchQuery('');
    }
  }, [isOpened]);

  // Cmd/Ctrl+K opens the menu and focuses the search box from anywhere.
  useEffect(() => {
    if (!isLoggedIn) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpened(true);
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLoggedIn]);

  const toggleOpenUploadReceiptOCR = useCallback(() => {
    setIsOpened(false);
    setOpenUploadReceiptOCR(!openUploadReceiptOCR);
  }, [setOpenUploadReceiptOCR, openUploadReceiptOCR]);

  const logo = (
    <div className="menu-logo flex items-center gap-3">
      {logoSrc && (
        // min-w-0: on a phone in development the badge beside it needs the room; it letterboxes.
        <img
          src={logoSrc}
          alt="Kalos Services Logo"
          className="h-10 w-auto min-w-0 object-contain"
        />
      )}
      {IS_DEVELOPMENT_ENVIRONMENT && (
        <Badge
          variant="outline"
          className="border-yellow-400 bg-yellow-100 px-3 py-1.5 text-sm font-semibold text-yellow-800"
        >
          Development Environment
        </Badge>
      )}
    </div>
  );

  const isAnalyticsAdmin = useMemo(
    () => user.permissionGroups.find((p) => p.name === 'AnalyticsAdmin'),
    [user.permissionGroups],
  );
  const isCustomerAccountAdmin = useMemo(
    () => user.permissionGroups.find((p) => p.name === 'CustomerAccountAdmin'),
    [user.permissionGroups],
  );
  const isDevTesting = useMemo(
    () => user.permissionGroups.find((p) => p.name === 'DevTesting'),
    [user.permissionGroups],
  );
  const isDispatch = useMemo(
    () => user.permissionGroups.find((p) => p.name === 'Dispatch'),
    [user.permissionGroups],
  );
  // Commercial techs work their calls from the Commercial Service queue, so
  // they get that page without the rest of Dispatch (86bckgt7j).
  const isCommercialTech = useMemo(
    () => COMMERCIAL_DEPARTMENTS.some((d) => d.id === user.employeeDepartmentID),
    [user.employeeDepartmentID],
  );
  // Coordinators and publishers, and the lodging desk, which reads the Overview tab.
  const canOpenProjectSchedule = useMemo(() => projectScheduleOpenCheck(user), [user]);
  const projectScheduleReleased = useFeatureFlag('project-schedule.module', {
    applies: canOpenProjectSchedule,
  });
  const isLodgingAdmin = useMemo(
    () => user.permissionGroups.find((p) => p.name === 'LodgingAdmin'),
    [user.permissionGroups],
  );
  const isMembershipAdmin = useMemo(
    () => user.permissionGroups.find((p) => p.name === 'MembershipAdmin'),
    [user.permissionGroups],
  );
  const isPermitAdmin = useMemo(() => {
    return user.permissionGroups.find((p) => p.name === 'PermitAdmin');
  }, [user.permissionGroups]);
  const isPermissionManager = useMemo(
    () => user.permissionGroups.find((p) => p.name === 'PermissionManager'),
    [user.permissionGroups],
  );
  const isProposalAdmin = useMemo(
    () => user.permissionGroups.find((p) => p.name === 'ProposalAdmin'),
    [user.permissionGroups],
  );
  // FeatureFlagAdmin, or the owner of a flag: the same rule as the page's route loader.
  const { rows: featureFlagRows } = useFeatureFlags();
  const canManageFeatureFlags = useMemo(
    () => !!user.id && canOpenFeatureFlags(user, featureFlagRows),
    [user, featureFlagRows],
  );

  const { data: thoughtBoxLink } = useGetInternalLinkQuery({
    id: 30,
    enabled: isLoggedIn,
  });

  const handleLogout = useCallback(() => {
    auth.signout(() => {
      router.navigate({ to: '/' });
    });
  }, [auth, router]);

  const new_menuItems = useMemo<MenuItem[]>(() => {
    const canShowAdminSection =
      isLodgingAdmin || isPermitAdmin || isFleetAdmin || isManager || canManageFeatureFlags;

    // ── Home ──
    const homeSectionItems: MenuItem[] = [
      {
        items: [
          {
            title: 'Dashboard',
            route: { to: '/home' },
            icon: <HomeIcon />,
          },
          {
            title: 'Service Calendar',
            route: { to: '/calendar' },
            icon: <CalendarIcon />,
          },
          ...(canOpenProjectSchedule && projectScheduleReleased
            ? [
                {
                  title: 'Project Scheduling',
                  route: { to: '/project-schedule' },
                  icon: <CalendarIcon />,
                } satisfies NavOption,
              ]
            : []),
        ],
        heading: (
          <div className="flex items-center gap-2">
            <HomeIcon className="size-4" />
            <span>Home</span>
          </div>
        ),
        localStorageKey: 'home',
      },
    ];

    // ── Profile ──
    const profileSectionItems: MenuItem[] = [
      {
        items: [
          {
            title: 'My Account',
            route: { to: '/my-account' },
            icon: <PersonIcon />,
          },
          {
            title: 'Timesheet',
            route: {
              to: '/timesheet/$timesheetOwnerId',
              params: { timesheetOwnerId: user.id },
            },
            icon: <ClockIcon />,
          },
          {
            title: 'Receipts',
            route: { to: '/transactions' },
            icon: <ReaderIcon />,
          },
          {
            title: 'Reimbursements',
            route: {
              to: '/reimbursements/$reimbursementOwnerId',
              params: { reimbursementOwnerId: user.id },
            },
            icon: <ReloadIcon />,
          },
          {
            title: 'Per Diem',
            route: { to: '/perdiem' },
            icon: <CounterClockwiseClockIcon />,
          },
          {
            title: 'Spiffs',
            route: { to: '/spiffLog' },
            icon: <FileIcon />,
          },
          ...(managerCheck(user)
            ? [
                {
                  title: 'Bonus Allocator',
                  route: { to: '/spiffLog/allocator' },
                  icon: <FileIcon />,
                } satisfies NavOption,
              ]
            : []),
          {
            title: 'My Tool Fund',
            route: { to: '/toolLog' },
            icon: <HammerIcon className="size-4" />,
          },
        ],
        heading: (
          <div className="flex items-center gap-2">
            <CircleUser className="size-4" />
            <span>Profile</span>
          </div>
        ),
        localStorageKey: 'profile',
      },
    ];

    // ── Customers ──
    const customersSectionItems: MenuItem[] = [
      {
        items: [
          {
            title: 'Directory',
            route: { to: '/search' },
            icon: <MagnifyingGlassIcon />,
          },
          {
            title: 'Jobs',
            route: { to: '/jobs-unified' },
            icon: <CubeIcon />,
          },
          {
            title: 'Appointment Scheduler',
            route: { to: '/appointment-scheduler' },
            icon: <CalendarIcon />,
          },
          ...(isMemo
            ? [
                {
                  title: 'Notes',
                  route: { to: '/notes' },
                  icon: <ArchiveIcon />,
                } satisfies NavOption,
              ]
            : []),
          {
            title: 'Memberships',
            route: { to: '/memberships' },
            icon: <BadgeCheck className="size-4" />,
          },
        ],
        heading: (
          <div className="flex items-center gap-2">
            <MagnifyingGlassIcon className="size-4" />
            <span>Customers</span>
          </div>
        ),
        localStorageKey: 'customers',
      },
    ];

    // ── Dispatch ──
    const commercialServiceItem: NavOption = {
      title: 'Commercial Service',
      route: { to: '/commercial-service-queue' },
      icon: <Building2 className="size-4" />,
    };
    const dispatchSectionItems: MenuItem[] = [
      {
        items: isDispatch
          ? [
              {
                title: 'Dispatch',
                route: { to: '/dispatch-new' },
                icon: <ClipboardIcon />,
              },
              {
                title: 'First Call',
                route: { to: '/first-calls' },
                icon: <AccessibilityIcon />,
              },
              commercialServiceItem,
            ]
          : [commercialServiceItem],
        heading: (
          <div className="flex items-center gap-2">
            <Headset className="size-4" />
            <span>Dispatch</span>
          </div>
        ),
        localStorageKey: 'dispatch',
      },
    ];

    // ── Reporting ──
    const reportingSectionItems: MenuItem[] = [
      {
        items: [
          ...(isCustomerAccountAdmin
            ? [
                {
                  title: 'Accounts',
                  route: { to: '/accounts' },
                  icon: <Building2 className="size-4" />,
                } satisfies NavOption,
              ]
            : []),
          ...(isAnalyticsAdmin
            ? [
                {
                  title: 'Analytics',
                  route: { to: '/analytics' },
                  icon: <ChartColumn className="size-4" />,
                } satisfies NavOption,
              ]
            : []),
          ...(isDevTesting && isMembershipAdmin
            ? [
                {
                  title: 'Membership Report',
                  route: { to: '/membership-report' },
                  icon: <BadgeDollarSign className="size-4" />,
                } satisfies NavOption,
              ]
            : []),
          ...(isProposalAdmin
            ? [
                {
                  title: 'Proposals',
                  route: { to: '/proposals' as const },
                  icon: <FileIcon />,
                } satisfies NavOption,
              ]
            : []),
          ...(reportsMenuAccessCheck(user)
            ? [
                {
                  title: 'Reports',
                  route: { to: '/reports' },
                  icon: <BookmarkIcon />,
                } satisfies NavOption,
              ]
            : []),
        ],
        heading: (
          <div className="flex items-center gap-2">
            <BarChartIcon className="size-4" />
            <span>Reporting</span>
          </div>
        ),
        localStorageKey: 'reporting',
      },
    ];

    // ── Team ──
    const teamSectionItems: MenuItem[] = [
      {
        items: [
          {
            title: 'Employees',
            route: { to: '/employees' },
            icon: <PersonIcon />,
          },
          ...(isManager
            ? [
                {
                  title: 'Payroll',
                  route: { to: '/payroll' },
                  icon: <BarChartIcon />,
                } satisfies NavOption,
              ]
            : []),
          {
            title: 'HR',
            route: { to: '/hrDashboard' },
            icon: <ArchiveIcon />,
          },
          {
            title: 'Kalos Knowledge Hub',
            icon: <ReaderIcon />,
            href: 'https://www.kb.kalos.live/',
            target: '_blank',
          } satisfies NavOption,
        ],
        heading: (
          <div className="flex items-center gap-2">
            <PersonIcon className="size-4" />
            <span>Team</span>
          </div>
        ),
        localStorageKey: 'team',
      },
    ];

    // ── Finance ──
    const financeSectionItems: MenuItem[] = [
      {
        items: [
          ...(isManager
            ? [
                {
                  title: 'Billing',
                  route: { to: '/callsPending' },
                  icon: <EnvelopeClosedIcon />,
                } satisfies NavOption,
              ]
            : []),
          {
            title: 'Accounts Payable',
            route: { to: '/accountsPayable' },
            icon: <BackpackIcon />,
          } satisfies NavOption,
          ...(isManager || isTransactionValidator
            ? [
                {
                  title: 'Transactions',
                  route: { to: '/transactionAdmin' },
                  icon: <ReaderIcon />,
                } satisfies NavOption,
              ]
            : []),
          ...(isManager
            ? [
                {
                  title: 'Tool Fund',
                  route: { to: '/toolFundDashboard' },
                  icon: <Hammer className="size-4" />,
                } satisfies NavOption,
              ]
            : []),
        ],
        heading: (
          <div className="flex items-center gap-2">
            <BackpackIcon className="size-4" />
            <span>Finance</span>
          </div>
        ),
        localStorageKey: 'finance',
      },
    ];

    // ── Admin Resources ──
    const adminSectionItems: MenuItem[] = [
      {
        items: [
          ...(isLodgingAdmin
            ? [
                {
                  title: 'Lodging Admin',
                  route: { to: '/lodgingAdmin' },
                  icon: <TentIcon size={15} />,
                } satisfies NavOption,
              ]
            : []),
          ...(isPermitAdmin
            ? [
                {
                  title: 'Permitting',
                  route: { to: '/permitting' },
                  icon: <FileCheck className="size-4" />,
                } satisfies NavOption,
              ]
            : []),
          ...(isFleetAdmin
            ? [
                {
                  title: 'Fleet Admin',
                  route: { to: '/vehicles' },
                  icon: <Car className="size-4" />,
                } satisfies NavOption,
              ]
            : []),
          ...(canManageFeatureFlags
            ? [
                {
                  title: 'Feature Flags',
                  route: { to: '/admin/feature-flags' },
                  icon: <FlagIcon className="size-4" />,
                } satisfies NavOption,
              ]
            : []),
        ],
        heading: (
          <div className="flex items-center gap-2">
            <ShieldIcon className="size-4" />
            <span>Admin Resources</span>
          </div>
        ),
        localStorageKey: 'admin',
      },
    ];

    // ── Tools ──
    const toolsSectionItems: MenuItem[] = [
      {
        items: [
          {
            title: 'Resources',
            route: { to: '/documents' },
            icon: <StackIcon />,
          },
          {
            title: 'Project Forms',
            route: { to: '/projectResources' },
            icon: <ClipboardIcon />,
          } satisfies NavOption,
          {
            title: 'Help Desk',
            icon: <MessageCircleQuestion size={15} />,
            button: true,
            onClick: () => window.open('/helpDesk', '_blank'),
          } satisfies NavOption,
          ...(isPermissionManager
            ? [
                {
                  title: 'Permissions Manager',
                  route: { to: '/permissionsManager' },
                  icon: <KeyRound className="size-4" />,
                } satisfies NavOption,
              ]
            : []),
          {
            title: 'Upload Photo OCR',
            icon: <CameraIcon />,
            onClick: toggleOpenUploadReceiptOCR,
            button: true,
          } satisfies NavOption,
          {
            title: 'ThoughtBox',
            button: true,
            onClick: () => window.open(thoughtBoxLink?.url ?? '', '_blank'),
            icon: <Calendar className="size-4" />,
          },
        ],
        heading: (
          <div className="flex items-center gap-2">
            <HammerIcon className="size-4" />
            <span>Tools</span>
          </div>
        ),
        localStorageKey: 'tools',
      },
    ];

    // ── Assemble menu ──
    const showReporting =
      isCustomerAccountAdmin ||
      isAnalyticsAdmin ||
      isManager ||
      isProposalAdmin ||
      reportsMenuAccessCheck(user) ||
      (isDevTesting && isMembershipAdmin);

    const result: MenuItem[] = [
      ...(user.isEmployee === 1
        ? ([
            ...homeSectionItems,
            ...profileSectionItems,
            ...customersSectionItems,
          ] satisfies MenuItem[])
        : []),

      ...(isDispatch || isCommercialTech ? dispatchSectionItems : []),

      ...(showReporting ? reportingSectionItems : []),

      ...(user.isEmployee === 1 ? teamSectionItems : []),

      ...financeSectionItems,

      ...(canShowAdminSection ? adminSectionItems : []),

      ...toolsSectionItems,

      {
        title: 'Logout',
        button: true,
        onClick: handleLogout,
        icon: <ExitIcon />,
      },
    ];

    return result;
  }, [
    canManageFeatureFlags,
    isAnalyticsAdmin,
    isCustomerAccountAdmin,
    isDevTesting,
    isDispatch,
    isCommercialTech,
    isFleetAdmin,
    isLodgingAdmin,
    isManager,
    isMembershipAdmin,
    isMemo,
    isPermissionManager,
    isPermitAdmin,
    canOpenProjectSchedule,
    projectScheduleReleased,
    isProposalAdmin,
    isTransactionValidator,
    toggleOpenUploadReceiptOCR,
    user,
    thoughtBoxLink?.url,
    handleLogout,
  ]);

  const flattenedMenuNavOptions = useMemo(
    () => flattenVisibleMenuNavOptions(new_menuItems, user),
    [new_menuItems, user],
  );

  const filteredMenuSearchResults = useMemo(
    () =>
      flattenedMenuNavOptions.filter(({ nav, sectionLabel, keywords }) =>
        menuSearchMatches(menuSearchQuery, nav.title, sectionLabel, keywords),
      ),
    [flattenedMenuNavOptions, menuSearchQuery],
  );

  const groupedMenuSearchResults = useMemo(() => {
    const groups = new Map<string, FlattenedMenuNavOption[]>();
    for (const entry of filteredMenuSearchResults) {
      const existing = groups.get(entry.sectionLabel);
      if (existing) {
        existing.push(entry);
      } else {
        groups.set(entry.sectionLabel, [entry]);
      }
    }
    return Array.from(groups, ([label, items]) => ({ label, items }));
  }, [filteredMenuSearchResults]);

  const isMenuSearchActive = menuSearchQuery.trim().length > 0;

  return (
    <ThemeProvider theme={customTheme.lightTheme}>
      <div className="menu-container flex w-full items-center">
        <div className="flex items-center">
          {isLoggedIn && (
            <Sheet open={isOpened} onOpenChange={setIsOpened}>
              <SheetTrigger asChild>
                <Button className="mr-6 flex items-center gap-2" variant="secondary">
                  <HamburgerMenuIcon className="h-4 w-4" />
                  <span>Menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="h-full w-[18rem] overflow-hidden p-0 md:p-0"
                closeButtonClassName="right-3"
                onOpenAutoFocus={(e) => {
                  e.preventDefault();
                  searchInputRef.current?.focus();
                }}
              >
                <SheetHeader className="space-y-2 px-3 pt-4">
                  <SheetTitle className="flex min-h-9 items-center pr-14 text-sm font-semibold">
                    Kalos Menu
                  </SheetTitle>
                  <Input
                    ref={searchInputRef}
                    ignoreScrolling
                    type="search"
                    enterKeyHint="search"
                    placeholder="Search menu…"
                    value={menuSearchQuery}
                    onChange={(e) => setMenuSearchQuery(e.target.value)}
                    aria-label="Search menu"
                    className="text-sm"
                  />
                </SheetHeader>
                <ScrollArea className="h-[calc(100vh-6rem)] min-w-0 overflow-y-auto overflow-x-hidden">
                  {isMenuSearchActive ? (
                    <ul className="min-w-0 space-y-2 p-3 pr-4 pt-1">
                      {filteredMenuSearchResults.length === 0 ? (
                        <li className="text-muted-foreground list-none px-3 text-sm">
                          No menu items match your search.
                        </li>
                      ) : (
                        groupedMenuSearchResults.map((group, groupIdx) => (
                          <li
                            key={`${group.label}-${groupIdx}`}
                            className="flex min-w-0 list-none flex-col gap-0.5"
                          >
                            {group.label && (
                              <span className="text-muted-foreground px-3 text-xs">
                                {group.label}
                              </span>
                            )}
                            <ul className="min-w-0 space-y-0.5">
                              {group.items.map((entry, idx) => (
                                <NavOptionComponent
                                  key={`${group.label}-${entry.nav.title}-${idx}`}
                                  item={entry.nav}
                                  isSubModule={entry.isSubModule}
                                />
                              ))}
                            </ul>
                          </li>
                        ))
                      )}
                    </ul>
                  ) : (
                    <ul className="min-w-0 space-y-1 p-3 pr-4 pt-1">
                      {new_menuItems.map((item, idx) => (
                        <KalosMenuItem key={idx} item={item} />
                      ))}
                    </ul>
                  )}
                </ScrollArea>
              </SheetContent>
            </Sheet>
          )}

          {isLoggedIn ? <Link to="/dashboard-home">{logo}</Link> : <div>{logo}</div>}
        </div>

        {openUploadReceiptOCR && (
          <Modal open onClose={toggleOpenUploadReceiptOCR}>
            <UploadPhotoOCR title="Upload Photo OCR" onClose={toggleOpenUploadReceiptOCR} />
          </Modal>
        )}
      </div>
    </ThemeProvider>
  );
};

export default SideMenu;
